import classNames from 'classnames';
import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { Redirect } from 'react-router-dom';
import { branch, defaultProps, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators, compose } from '../../../../../node_modules/redux';
import { eventSorting, routes, searchTileType } from '../../../../constants';
import { isManager, mapRecommendedResult, mapUserCpdCategoryProgress } from '../../../../services';
import { cpdCategorysActions } from '../../../../state/modules/areas-of-advice';
import { publisherActions } from '../../../../state/modules/publisher';
import { recommendedSearchTileSelector, searchTileActions } from '../../../../state/modules/search-tile';
import { currentUserCpdDaysRemaining, currentUserSelector, userActions } from '../../../../state/modules/user';
import { EventFilterGroup, FormField, SearchToggler } from '../../../components';
import Loading from '../../loading';
import ProgressCard from '../partials/progress-card';
import ContentList, { searchTileFilter } from './partials/content-list';

const SLIDES_PER_SECTION = 10;

const EventHome = (props) => (
  <div className='base-layout'>
    <section id='header--training' className='layout--header'>
      <Grid>
        <Row className='no-gutter'>
          <Col xs={12} md={7}>
            <Breadcrumb>
              <LinkContainer to={routes.app.training.eventHome}>
                <BreadcrumbItem active>
                  Events
                </BreadcrumbItem>
              </LinkContainer>
            </Breadcrumb>
            <h1>
              {
                props.viewPastEvents ?
                  'Past Events' :
                  'Upcoming Events'
              }
            </h1>
            <Row className='no-gutter'>
              <Col xs={12} md={6} className='no-padding top-gap flexbox nowrap'>
                <span className='right-gap'>
                  <SearchToggler onChange={props.onSearchChange} />
                </span>
                <EventFilterGroup {...{
                  onApply: props.updateFilters
                }} />
              </Col>
              <Col xs={12} md={5} mdOffset={1} className='no-padding'>
                <form className={classNames('searchToggle searchDesktop', { block: props.displaySearchBlock })}>
                  <FormField id='sort-by' label='Sort by' type='select-item' required>
                    <select
                      id='sort-by'
                      value={props.sort}
                      required
                      onChange={props.onSortByChange}>
                      {
                        fp.map((sortingKey) =>
                          <option key={sortingKey} value={sortingKey}>{fp.get(sortingKey, eventSorting).displayName}</option>
                        )(fp.keys(eventSorting))
                      }
                    </select>
                  </FormField>
                </form>
              </Col>
            </Row>
          </Col>
          <ProgressCard {...{ ...props.overallCpdProgress, showExternalCpd: false }} />
          <Col xs={12} md={4} mdOffset={1}>
            <Row>
              <Col xs={8} xsOffset={2}>
                <Button className='large full top-gap' onClick={props.toggleViewPastEvent}>
                  {
                    props.viewPastEvents ?
                      'View upcoming events' :
                      'View past events'
                  }
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Grid>
    </section>
    <ComposedEventsSection {...props} />
  </div>
);

const SearchResult = ({ filter }) => <ContentList filter={filter} />

const ComposedEventsSection = compose(
  branch(
    ({ isFetchingCpdCategory, isFetchingUserCpdCategorys }) => isFetchingCpdCategory || isFetchingUserCpdCategorys,
    renderComponent(Loading)
  )
)(SearchResult);

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  cpdDaysRemaining: currentUserCpdDaysRemaining(state),
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  isFetchingCpdCategorys: state.cpdCategorys.isFetching,
  publishers: state.publisher.publishers,
  isFetchingPublishers: state.publisher.isFetching,
  userCpdCategoryProgress: state.user.cpdCategoryProgress,
  userCpdCategories: state.user.cpdCategories,
  isFetchingUserCpdCategorys: state.user.currentUserCpdCategorysIsFetching,
  recommendedEvents: mapRecommendedResult(state.industry.cpdCategorys, state.publisher.publishers),
  isFetchingEvents: state.searchTile.isFetching,
  industryCpdCategorys: state.industry.cpdCategorys,
  userState: state.user,
  cpdCategoryEvents: recommendedSearchTileSelector(state)
});

const mapDispatchToProps = (dispatch) => ({
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getEvents: bindActionCreators(searchTileActions.searchTile.get, dispatch),
  getRecommended: bindActionCreators(searchTileActions.searchTile.getRecommended, dispatch),
  getUserCpdCategorys: bindActionCreators(userActions.user.getCpdCategorys, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ currentUser }) => isManager(currentUser),
    renderComponent(() => (
      <Redirect to={routes.app.teams.root} />
    ))
  ),
  withStateHandlers(
    {
      initiatedEventLoading: false,
      displaySearchBlock: false,
      searchQuery: '',
      sort: 'startDateAsc',
      searchDebounce: undefined,
      viewPastEvents: false,
      filters: {}
    },
    {
      updateFilters: () => (filters) => ({ filters }),
      updateSearchDebound: () => (searchDebounce) => ({ searchDebounce }),
      onSortByChange: () => (e) => ({
        sort: e.target.value
      }),
      onSearchChange: () => (query) => ({
        searchQuery: query,
      }),
      onSearchIconClick: ({ displaySearchBlock }) => () => ({
        displaySearchBlock: !displaySearchBlock
      }),
      contentLoadingInitiated: () => () => ({
        initiatedEventLoading: true
      }),
      toggleViewPastEvent: ({ viewPastEvents }) => () => ({
        viewPastEvents: !viewPastEvents
      })
    }
  ),
  withProps(
    ({
      searchQuery,
      sort,
      viewPastEvents,
      filters
    }) => {
      const cpdCategories = fp.join(',')(filters.cpdCategoryIds);
      const associations = fp.join(',')(filters.associations);
      const locations = fp.join(',')(filters.locations);
      const emptyUserCpdCategorys = null;
      const emptyPublishers = null;
      const emptyBusinessId = null;
      const emptyContentType = null;
      const emptyIsBookmarkedOnly = null;
      const filter = searchTileFilter(
        searchTileType.Event,
        cpdCategories,
        sort,
        emptyUserCpdCategorys,
        searchQuery,
        emptyPublishers,
        emptyBusinessId,
        associations,
        locations,
        filters.min,
        filters.max,
        emptyContentType,
        emptyIsBookmarkedOnly,
      )
      const status = viewPastEvents ? 'Archived' : undefined
      return {
        filter: {
          ...filter,
          status,
          goals: fp.join(',')(filters.goalIds)
        }
      }
    }
  ),
  defaultProps(
    {
      userCpdCategoryProgress: {}
    }
  ),
  withProps(({ cpdCategorys, userCpdCategoryProgress, userState }) => ({
    mappedCpdCategorys: fp.filter(
      ({ hasChild }) => !hasChild
    )(mapUserCpdCategoryProgress(cpdCategorys)(userCpdCategoryProgress)),
    userCpdCategoryIds: userState.currentUserCpdCategorys,
    allCpdCategorys: fp.map((cpdCategory) => ({
      cpdCategory: cpdCategory.id,
      title: cpdCategory.name
    }))(cpdCategorys),
    overallCpdProgress: {
      totalCpdCategorys: fp.get('cpdCategorys.length')(userCpdCategoryProgress),
      ...fp.get('primaryStat')(userCpdCategoryProgress),
      ...fp.get('derivedStat')(userCpdCategoryProgress),
    }
  })),
  withHandlers({
    getRecommended: ({ getRecommended }) => () => {
      getRecommended({ take: SLIDES_PER_SECTION, searchTileType: searchTileType.Event });
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        cpdCategory,
        isFetchingCpdCategory,
        mappedCpdCategorys,
        publishers,
        isFetchingPublishers,
        getCpdCategorys,
        getPublishers,
        getUserCpdCategorys,
        getRecommended,
        getEvents,
        filter
      } = this.props;

      getEvents(0, filter);

      if (fp.isEmpty(cpdCategory) && !isFetchingCpdCategory) {
        getCpdCategorys();
      }
      if (fp.isEmpty(publishers) && !isFetchingPublishers) {
        getPublishers();
      }
      if (fp.isEmpty(mappedCpdCategorys)) {
        getUserCpdCategorys();
      } else {
        getRecommended(fp.map((x) => x.cpdCategoryId)(mappedCpdCategorys));
      }
    },
    componentDidUpdate(prevProps) {
      const {
        getEvents,
        filter,
        searchDebounce,
        updateSearchDebound
      } = this.props

      if (!fp.isEqual(filter)(prevProps.filter)) {
        const debounced = fp.debounce(500)(() => {
          getEvents(0, filter);
        });
        searchDebounce && searchDebounce.cancel();
        updateSearchDebound(debounced)
        debounced();
      }
    },
    componentWillReceiveProps(nextProps) {
      const {
        isFetchingEvents,
        userCpdCategories,
        cpdCategorys,
        isFetchingUserCpdCategorys,
        isFetchingCpdCategorys,
        getRecommended
      } = this.props;

      const { initiatedEventLoading } = nextProps;

      const fetchedUserCpdCategorys = fp.difference(userCpdCategories, nextProps.userCpdCategories);

      // After loading the areas of advice for the user and the site, check to see if the user
      // has any CpdCategory selected. If they have none set up then do a content load on all the content, otherwise
      // always use the current user's selected CpdCategory setting. 
      const cpdCategoryIds = (
        !isFetchingUserCpdCategorys &&
        !isFetchingCpdCategorys &&
        nextProps.userCpdCategories.length === 0 &&
        nextProps.cpdCategorys.length !== 0
      ) ?
        fp.map((x) => x.id)(cpdCategorys) :
        fp.map((x) => x.cpdCategoryId)(fetchedUserCpdCategorys);

      // Initiate the fisrt loading of the area of advice's content if:
      //  # it has not been initiated before, and
      //  # area of advices have some content to load.
      if (!initiatedEventLoading && !fp.isEmpty(cpdCategoryIds) && !isFetchingEvents) {
        getRecommended();
      }
    }
  })
)(EventHome)


// WEBPACK FOOTER //
// ./src/containers/pages/app/training/event-home.js